import React, { useContext } from 'react';
import {
  GridActionsCellItem,
  GridColDef,
  GridColumnHeaderParams,
  GridPreProcessEditCellProps,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridRowModes,
} from '@mui/x-data-grid-pro';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CancelIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@eposnow/ui-core';
import { useTheme } from '@mui/material/styles';
import nameValidationSchema from './validationSchema';
import { SidekickTablesContext } from '../../helpers/SidekickTablesContext';
import GenericGridContent from '../../../../../components/datagridComponents/GenericGridContent';
import GridCellEdit from '../../../../../components/datagridComponents/GridCellEdit';
import GridCellEditCheckbox from '../../../../../components/datagridComponents/GridCellEditCheckbox';

const useBuildColumns = (): GridColDef[] => {
  const { t } = useTranslation();
  const {
    handleAddClick,
    handleEditClick,
    handleDuplicateClick,
    handleSaveClick,
    handleDeleteClick,
    handleCancelClick,
    rowModesModel,
  } = useContext(SidekickTablesContext);
  const theme = useTheme();

  return [
    {
      field: 'name',
      sortable: false,
      flex: 1,
      minWidth: 100,
      editable: true,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) =>
        nameValidationSchema
          .isValid({
            name: params?.props?.value,
          })
          .then((valid) => ({ ...params.props, error: !valid }))
          .catch(() => ({ ...params.props })),
      renderHeader: (_params: GridColumnHeaderParams) => (
        <GenericGridContent
          content={[
            {
              text: t('screens.products.sidekick.import.productsTable.name'),
              shouldTranslate: true,
              typographyProps: {
                variant: 'boldText',
              },
            },
          ]}
        />
      ),
      renderCell: (params: GridRenderCellParams) => (
        <GenericGridContent
          content={[
            {
              text: params.value,
              shouldTranslate: false,
              shouldClipContent: true,
              typographyProps: {
                variant: 'body2',
              },
            },
          ]}
        />
      ),
      renderEditCell: (params: GridRenderEditCellParams) => (
        <GridCellEdit
          gridRenderParams={params}
          labelKey={t('screens.products.sidekick.import.productsTable.name')}
          validationSchema={nameValidationSchema}
        />
      ),
    },

    {
      field: 'isWet',
      sortable: false,
      editable: true,
      flex: 0.5,
      minWidth: 100,
      renderHeader: (_params: GridColumnHeaderParams) => (
        <GenericGridContent
          content={[
            {
              text: t('screens.products.sidekick.import.productsTable.wetDry'),
              shouldTranslate: true,
              typographyProps: {
                variant: 'boldText',
              },
            },
          ]}
        />
      ),
      renderCell: (params: GridRenderCellParams) => (
        <GenericGridContent
          content={[
            {
              text: params.value
                ? t('screens.products.sidekick.import.productsTable.wet')
                : t('screens.products.sidekick.import.productsTable.dry'),
              shouldTranslate: false,
              typographyProps: {
                variant: 'body2',
              },
            },
          ]}
        />
      ),
      renderEditCell: (params: GridRenderEditCellParams) => (
        <GridCellEditCheckbox
          gridRenderParams={params}
          labelKey="screens.products.sidekick.import.productsTable.isWet"
          id={`isWet${params.id}`}
        />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      flex: 0.5,
      minWidth: 200,
      renderHeader: (_params: GridColumnHeaderParams) => (
        <GenericGridContent
          content={[
            {
              text: 'common.actions',
              shouldTranslate: true,
              typographyProps: {
                variant: 'boldText',
              },
            },
          ]}
        />
      ),
      align: 'right',
      headerAlign: 'right',
      hideSortIcons: true,
      headerClassName: 'hideRightSeparator',
      getActions: ({ id }) => {
        const newId = id as number;
        const isInEditMode = rowModesModel[newId]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <Tooltip title={t('screens.products.sidekick.import.productsTable.save')} theme={theme}>
              <GridActionsCellItem
                icon={<SaveIcon />}
                label={t('screens.products.sidekick.import.productsTable.save')}
                data-qa-id="actions-save"
                onClick={() => handleSaveClick(newId)}
                color="inherit"
              />
            </Tooltip>,
            <Tooltip title={t('screens.products.sidekick.import.productsTable.cancel')} theme={theme}>
              <GridActionsCellItem
                icon={<CancelIcon />}
                label={t('screens.products.sidekick.import.productsTable.cancel')}
                data-qa-id="actions-cancel"
                onClick={() => handleCancelClick(newId)}
                color="inherit"
              />
            </Tooltip>,
          ];
        }

        return [
          <Tooltip title={t('screens.products.sidekick.import.productsTable.add')} theme={theme}>
            <GridActionsCellItem
              icon={<AddIcon />}
              label={t('screens.products.sidekick.import.productsTable.add')}
              data-qa-id="actions-add"
              onClick={() => handleAddClick(newId)}
              color="inherit"
            />
          </Tooltip>,
          <Tooltip title={t('screens.products.sidekick.import.productsTable.duplicate')} theme={theme}>
            <GridActionsCellItem
              icon={<ContentCopyIcon />}
              label={t('screens.products.sidekick.import.productsTable.duplicate')}
              data-qa-id="actions-duplicate"
              onClick={() => handleDuplicateClick(newId)}
              color="inherit"
            />
          </Tooltip>,
          <Tooltip title={t('screens.products.sidekick.import.productsTable.edit')} theme={theme}>
            <GridActionsCellItem
              icon={<EditIcon />}
              label={t('screens.products.sidekick.import.productsTable.edit')}
              data-qa-id="actions-edit"
              onClick={() => handleEditClick(newId)}
              color="inherit"
            />
          </Tooltip>,
          <Tooltip title={t('screens.products.sidekick.import.productsTable.delete')} theme={theme}>
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label={t('screens.products.sidekick.import.productsTable.delete')}
              data-qa-id="actions-delete"
              onClick={() => handleDeleteClick(newId)}
              color="inherit"
            />
          </Tooltip>,
        ];
      },
    },
  ];
};

export default useBuildColumns;
